import { styled } from "../../../../styles";

/**
 * Long-text editing for the /admin pages (EN-5161 + EN-5162). Both tickets
 * asked for a design decision on "editing long text" and asked for the two to
 * be aligned; this is that one treatment.
 */

/**
 * Height of a split editor/preview pane.
 *
 * Measured from the viewport minus the chrome the editor sits inside — the
 * top panel, the page header, the filter bar, the subject row, the
 * placeholder tray and the card's own footer — so the whole page fits a
 * screen instead of the pane pushing its Save button past the fold. Floored
 * for a short laptop and capped so it stays a readable column on a large
 * monitor. (The footer is sticky as well: the textarea is user-resizable, so
 * no fixed height can be the only guard.)
 */
const SPLIT_PANE_HEIGHT = "clamp(360px, calc(100vh - 560px), 820px)";

export const EditorContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  minWidth: 0,
  flex: 1,
});

/**
 * Footer under the textarea: the hint on the left, the character count hard
 * against the right edge. The count used to sit up beside the field label,
 * where it read as part of the heading rather than as a fact about what is in
 * the box (EN-5146 UI feedback).
 */
export const EditorFooter = styled("div", {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: "12px",
});

export const EditorCount = styled("span", {
  marginLeft: "auto",
  flexShrink: 0,
  fontFamily: "$openSans",
  fontSize: "11px",
  color: "$gray-80",
  fontVariantNumeric: "tabular-nums",
});

/**
 * Monospace, resizable vertically, and `white-space: pre` semantics come free
 * with a textarea — the prompt bodies and email HTML must round-trip byte for
 * byte, so nothing here trims or reflows the value.
 */
export const EditorTextArea = styled("textarea", {
  width: "100%",
  minHeight: "220px",
  resize: "vertical",
  padding: "10px 12px",
  borderRadius: "6px",
  border: "1px solid $gray-40",
  backgroundColor: "$white",
  color: "$charcoal",
  fontFamily: "monospace",
  fontSize: "13px",
  lineHeight: 1.5,
  tabSize: 2,
  whiteSpace: "pre",
  overflowWrap: "normal",
  overflowX: "auto",

  "&:focus": {
    outline: "none",
    borderColor: "$primary",
  },

  "&:disabled": {
    backgroundColor: "$gray-20",
    cursor: "not-allowed",
  },

  variants: {
    /** Fills a split editor/preview pane. */
    tall: {
      true: { minHeight: SPLIT_PANE_HEIGHT },
    },
    /** Soft-wraps instead of scrolling sideways — better for prose prompts. */
    wrap: {
      true: {
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        overflowX: "hidden",
      },
    },
  },
});

/** Clickable list of the placeholders a template may reference. */
export const VariableList = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  gap: "6px",
  alignItems: "center",
  padding: "10px 12px",
  borderRadius: "6px",
  border: "1px solid $gray-40",
  backgroundColor: "$gray-20",
});

/** Says what the pills beside it are, so they read as actions. */
export const VariableListLabel = styled("span", {
  marginRight: "2px",
  fontFamily: "$openSans",
  fontSize: "12px",
  fontWeight: 500,
  color: "$gray-100",
});

export const VariableChip = styled("button", {
  padding: "4px 10px",
  borderRadius: "999px",
  border: "1px solid $primary-30",
  backgroundColor: "$primary-20",
  color: "$primary-80",
  fontFamily: "monospace",
  fontSize: "12px",
  lineHeight: 1.4,
  cursor: "pointer",
  transition: "background-color 0.15s, color 0.15s",

  "&:hover:not(:disabled)": {
    backgroundColor: "$primary",
    borderColor: "$primary",
    color: "$white",
  },

  "&:focus-visible": {
    outline: "2px solid $primary-40",
    outlineOffset: "1px",
  },

  "&:disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
  },
});

/** Editor beside its live preview; stacks when there isn't room. */
export const SplitPane = styled("div", {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
  gap: "16px",
  alignItems: "stretch",

  "@media screen and (max-width: 1000px)": {
    gridTemplateColumns: "minmax(0, 1fr)",
  },
});

export const PreviewPane = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  minWidth: 0,
});

/**
 * The preview is a sandboxed iframe with no `allow-scripts`, so a template can
 * render but cannot run anything or reach the portal's origin. Email HTML is
 * operator-supplied and this is an admin surface, but rendering it inline
 * would put arbitrary markup in the portal's own document.
 */
export const PreviewFrame = styled("iframe", {
  width: "100%",
  // Fills whatever the pane is: the editor beside it can be dragged taller,
  // and `SplitPane` stretches both columns to the row.
  flex: 1,
  minHeight: SPLIT_PANE_HEIGHT,
  border: "1px solid $gray-40",
  borderRadius: "6px",
  backgroundColor: "$white",
});
